import { motion } from 'framer-motion';
import { CheckCircle, Clock, Globe, MapPin, Phone } from 'lucide-react';
import { Suspense } from 'react';
import { Location } from '../../../location/LocationStep';
import Map from './Map';

// Define default colors to use if not provided
const defaultColors = {
  cardBackground: 'rgba(0, 0, 0, 0.95)',
  cardBorder: '#DDB88919',
  cardBorderSelected: '#DDB88949',
  cardShadow: 'rgba(221,184,137,0.07)',
  cardShadowSelected: 'rgba(221,184,137,0.15)',
  titleText: '#DDB889',
  subtitleText: '#9CA3AF',
  infoText: '#9CA3AF',
  iconColor: '#DDB889B2',
  selectionBackground: '#22c55e',
  selectionIcon: '#FFFFFF',
  mapBackground: '#00000066',
  mapPinColor: '#DDB88966'
};

interface LocationCardProps {
  locations: { node: Location }[];
  isClickable?: boolean;
  onLocationClick?: (location: Location) => void;
  selectedLocations?: Location | Location[] | null;
  showMap?: boolean;
  colors?: {
    cardBackground?: string;
    cardBorder?: string;
    cardBorderSelected?: string;
    cardShadow?: string;
    cardShadowSelected?: string;
    titleText?: string;
    subtitleText?: string;
    infoText?: string;
    iconColor?: string;
    selectionBackground?: string;
    selectionIcon?: string;
    mapBackground?: string;
    mapPinColor?: string;
  };
  itemsPerRow?: number;
}

const LocationCard: React.FC<LocationCardProps> = ({
  locations,
  isClickable = false,
  onLocationClick,
  selectedLocations = null,
  showMap = true,
  colors = {},
  itemsPerRow = 2
}) => {

  // Merge provided colors with defaults
  const themeColors = { ...defaultColors, ...colors };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const locationVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const isLocationSelected = (location: Location) => {
    if (!selectedLocations) return false;
    if (Array.isArray(selectedLocations)) {
      return selectedLocations.some(selected => selected.id === location.id);
    }
    return selectedLocations.id === location.id;
  };

  const handleLocationClick = (location: Location) => {
    if (!isClickable || !onLocationClick) return;
    onLocationClick(location);
  };

  // Function to get shadow style based on selection state
  const getShadowStyle = (location: Location) => {
    return isLocationSelected(location)
      ? themeColors.cardShadowSelected || 'rgba(221,184,137,0.15)'
      : themeColors.cardShadow || 'rgba(221,184,137,0.07)';
  };

  // Function to get border color based on selection state
  const getBorderColor = (location: Location) => {
    return isLocationSelected(location)
      ? themeColors.cardBorderSelected || '#DDB88949'
      : themeColors.cardBorder || '#DDB88919';
  };

    const getHoverShadowStyle = () => {
      return `0 0 20px ${themeColors.cardShadowSelected}`;
    };

  // Determine grid layout class based on itemsPerRow
  const getGridLayoutClass = () => {
    switch (itemsPerRow) {
      case 1:
        return 'lp-grid-cols-1';
      case 2:
        return 'lp-grid-cols-1 md:lp-grid-cols-2';
      case 3:
        return 'lp-grid-cols-1 md:lp-grid-cols-2 lg:lp-grid-cols-3';
      case 4:
        return 'lp-grid-cols-1 md:lp-grid-cols-2 lg:lp-grid-cols-4';
      default:
        return 'lp-grid-cols-1 md:lp-grid-cols-2';
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className={`lp-grid lp-gap-6 lp-w-full ${getGridLayoutClass()}`}
    >
      {locations.map(({ node: location }) => (
        <motion.div
          key={location.id}
          variants={locationVariants}
          className={`
            lp-flex lp-flex-col
            lp-rounded-2xl
            lp-backdrop-blur-sm
            lp-border
            lp-relative
            lp-overflow-hidden
            lp-transition-all
            lp-duration-500
            ${isClickable ? 'lp-cursor-pointer' : ''}
          `}
          style={{
            backgroundColor: themeColors.cardBackground,
            borderColor: getBorderColor(location),
            boxShadow: `0 0 15px ${getShadowStyle(location)}`
          }}
          onClick={() => handleLocationClick(location)}
          whileHover={{
            boxShadow: getHoverShadowStyle()
          }}
          transition={{ duration: 0.2 }}
        >
          {/* Selection indicator */}
          {isLocationSelected(location) && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="lp-absolute lp-top-4 lp-right-4 lp-z-[1000]"
            >
              <motion.div
                className="lp-w-8 lp-h-8 lp-rounded-full lp-flex lp-items-center lp-justify-center"
                style={{ backgroundColor: themeColors.selectionBackground }}
                whileHover={{ rotate: 180 }}
                transition={{ duration: 0.3 }}
              >
                <CheckCircle
                  className="lp-w-5 lp-h-5"
                  style={{ color: themeColors.selectionIcon }}
                />
              </motion.div>
            </motion.div>
          )}

          {/* Map Section - Only shown if showMap is true */}
          {showMap && (
            <div className="lp-h-[200px] lp-relative">
              <Suspense fallback={
                <div
                  className="lp-w-full lp-h-full lp-backdrop-blur-sm lp-flex lp-items-center lp-justify-center"
                  style={{ backgroundColor: themeColors.mapBackground }}
                >
                  <MapPin
                    className="lp-w-12 lp-h-12 lp-animate-pulse"
                    style={{ color: themeColors.mapPinColor }}
                  />
                </div>
              }>
                <div className="lp-absolute lp-inset-0">
                  <Map
                    position={{
                      lat: parseFloat(location.lat),
                      lng: parseFloat(location.long)
                    }}
                    onLocationChange={() => {}}
                    disableInteraction={true}
                  />
                </div>
              </Suspense>
            </div>
          )}

          {/* Content Section */}
          <div
            className="lp-flex lp-flex-col lp-flex-grow lp-p-5 lp-bg-gradient-to-b"
            style={{
              background: `linear-gradient(to bottom, ${themeColors.cardBackground}, ${themeColors.cardBackground})`
            }}
          >
            <div className="lp-mb-3">
              <motion.h3
                className="lp-text-lg lp-font-medium lp-mb-1"
                style={{ color: themeColors.titleText }}
                initial={{ opacity: 0.8 }}
                whileHover={{ opacity: 1 }}
              >
                {location.city}
              </motion.h3>
              <p
                className="lp-text-sm"
                style={{ color: themeColors.subtitleText }}
              >
                {location.address}
              </p>
            </div>

            <div className="lp-grid lp-grid-cols-2 lp-gap-3">
              {[
                { icon: Phone, value: location.phone },
                { icon: Globe, value: location.country },
                { icon: MapPin, value: location.zip },
                { icon: Clock, value: location.timeZone }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="lp-flex lp-items-center lp-gap-2 lp-group"
                  whileHover={{ x: 2 }}
                  transition={{ duration: 0.2 }}
                >
                  <item.icon
                    className="lp-w-4 lp-h-4 group-hover:lp-opacity-100"
                    style={{
                      color: themeColors.iconColor,
                      opacity: 0.7
                    }}
                  />
                  <span
                    className="lp-text-xs lp-truncate group-hover:lp-opacity-100"
                    style={{
                      color: themeColors.infoText,
                      opacity: 0.8
                    }}
                  >
                    {item.value}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default LocationCard;
